import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Box,
  Grid,
  Stack,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { Link as RouterLink } from 'react-router-dom';

const ProgramsManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [availableSchoolYears, setAvailableSchoolYears] = useState([]);
  const [currentSchoolYear, setCurrentSchoolYear] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fee: 0,
    schoolYear: '',
    active: true,
    gradeRestrictions: {
      enabled: false,
      minGrade: 1,
      maxGrade: 12
    }
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get('/religious-education/settings');
      if (response.data) {
        const years = response.data.availableSchoolYears || [];
        const current = response.data.currentSchoolYear || '';
        setAvailableSchoolYears(years);
        setCurrentSchoolYear(current);
        setFilterYear(current);
        setFormData(prev => ({ ...prev, schoolYear: current }));
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterYear ? { schoolYear: filterYear } : {};
      const response = await axios.get('/programs', { params });
      setPrograms(response.data);
    } catch (err) {
      setError('Failed to fetch programs');
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  }, [filterYear]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (filterYear !== undefined) {
      fetchPrograms();
    }
  }, [filterYear, fetchPrograms]);

  const handleOpenDialog = (program = null) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        name: program.name,
        description: program.description || '',
        fee: program.fee,
        schoolYear: program.schoolYear,
        active: program.active,
        gradeRestrictions: program.gradeRestrictions || {
          enabled: false,
          minGrade: 1,
          maxGrade: 12
        }
      });
    } else {
      setEditingProgram(null);
      setFormData({
        name: '',
        description: '',
        fee: 0,
        schoolYear: currentSchoolYear,
        active: true,
        gradeRestrictions: {
          enabled: false,
          minGrade: 1,
          maxGrade: 12
        }
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProgram(null);
  };

  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingProgram) {
        await axios.put(`/programs/${editingProgram._id}`, formData);
        setSuccess('Program updated successfully');
      } else {
        await axios.post('/programs', formData);
        setSuccess('Program created successfully');
      }
      handleCloseDialog();
      fetchPrograms();
      // Refresh settings in case a new school year was added
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save program');
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) {
      return;
    }

    try {
      await axios.delete(`/programs/${programId}`);
      setSuccess('Program deleted successfully');
      fetchPrograms();
    } catch (err) {
      setError('Failed to delete program');
    }
  };

  const handleToggleActive = async (program) => {
    try {
      await axios.patch(`/programs/${program._id}/toggle-active`);
      fetchPrograms();
    } catch (err) {
      setError('Failed to update program status');
    }
  };

  // Generate school year options for dropdown
  const generateSchoolYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = -2; i <= 3; i++) {
      const startYear = currentYear + i;
      years.push(`${startYear}-${startYear + 1}`);
    }
    // Merge with available years and remove duplicates
    const allYears = [...new Set([...years, ...availableSchoolYears])].sort().reverse();
    return allYears;
  };

  if (loading && programs.length === 0) {
    return <Container sx={{ mt: 2 }}><Typography>Loading programs...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h5">
            Program Management
          </Typography>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/religious-education/settings"
            startIcon={<ArrowBackIcon />}
          >
            Back to Settings
          </Button>
        </Stack>

        {/* Filter and Add Button */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by School Year</InputLabel>
            <Select
              value={filterYear}
              label="Filter by School Year"
              onChange={(e) => setFilterYear(e.target.value)}
            >
              <MenuItem value="">All Years</MenuItem>
              {availableSchoolYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}{year === currentSchoolYear ? ' (Current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Program
          </Button>
        </Box>

        {/* Programs Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>School Year</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Grade Range</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No programs found. Click "Add Program" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                programs.map((program) => (
                  <TableRow key={program._id}>
                    <TableCell>
                      <Typography fontWeight="medium">{program.name}</Typography>
                      {program.description && (
                        <Typography variant="body2" color="text.secondary">
                          {program.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={program.schoolYear}
                        color={program.schoolYear === currentSchoolYear ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>${program.fee.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={program.active ? 'Active' : 'Inactive'}
                        color={program.active ? 'success' : 'default'}
                        size="small"
                        onClick={() => handleToggleActive(program)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      {program.gradeRestrictions?.enabled
                        ? `Grades ${program.gradeRestrictions.minGrade}-${program.gradeRestrictions.maxGrade}`
                        : 'All grades'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenDialog(program)} size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(program._id)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Program Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProgram ? 'Edit Program' : 'Add New Program'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Program Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>School Year</InputLabel>
                <Select
                  value={formData.schoolYear}
                  label="School Year"
                  onChange={(e) => handleFormChange('schoolYear', e.target.value)}
                  required
                >
                  {generateSchoolYearOptions().map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fee ($)"
                type="number"
                value={formData.fee}
                onChange={(e) => handleFormChange('fee', Number(e.target.value))}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => handleFormChange('active', e.target.checked)}
                  />
                }
                label="Active (available for registration)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.gradeRestrictions.enabled}
                    onChange={(e) => handleFormChange('gradeRestrictions.enabled', e.target.checked)}
                  />
                }
                label="Enable grade restrictions"
              />
            </Grid>
            {formData.gradeRestrictions.enabled && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Minimum Grade"
                    type="number"
                    value={formData.gradeRestrictions.minGrade}
                    onChange={(e) => handleFormChange('gradeRestrictions.minGrade', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 1, max: 12 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Maximum Grade"
                    type="number"
                    value={formData.gradeRestrictions.maxGrade}
                    onChange={(e) => handleFormChange('gradeRestrictions.maxGrade', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 1, max: 12 } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.schoolYear}
          >
            {editingProgram ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProgramsManagement;
